import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import IdeaCard from "@/components/ideas/IdeaCard";
import { Lightbulb, ArrowRight } from "lucide-react";
import { FEATURED_IDEA_ID, FEATURED_PLAN } from "@/lib/featured";

// Specific ideas to keep out of the homepage initiatives strip.
const EXCLUDED_IDEA_IDS = ["a0000000-0000-4000-8000-000000000001"];

const HomeIdeasStrip = () => {
  const { data: ideas = [], isLoading } = useQuery({
    queryKey: ["home-ideas-strip"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("status", "published")
        .not("title", "ilike", "%emerging market%")
        .not("id", "in", `(${EXCLUDED_IDEA_IDS.join(",")})`)
        .order("vote_count", { ascending: false })
        .limit(9);
      if (error) throw error;

      // Pin the featured idea (Prichard Plaza) first as the lead initiative
      // under the Financial Empowerment Action Plan.
      const rows = [...(data || [])];
      rows.sort((a: any, b: any) => {
        if (a.id === FEATURED_IDEA_ID) return -1;
        if (b.id === FEATURED_IDEA_ID) return 1;
        return 0;
      });

      const userIds = [...new Set(rows.map((i: any) => i.user_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", userIds);
      const map = new Map((profiles || []).map((p: any) => [p.user_id, p]));
      return rows.map((i: any) => ({ ...i, profiles: map.get(i.user_id) || null }));
    },
  });

  return (
    <section id="initiatives" className="py-16 bg-background scroll-mt-24">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 mb-8">
          <div>
            <div className="flex items-center gap-2 text-primary mb-2">
              <Lightbulb className="h-5 w-5" />
              <span className="text-xs uppercase tracking-wider font-semibold">
                Initiatives · {FEATURED_PLAN.shortTitle}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground">
              Initiatives advancing the plan
            </h2>
            <p className="text-muted-foreground mt-1 max-w-xl">
              Community-built ideas moving Mobile's Financial Empowerment Action Plan from
              paper into practice — starting with Prichard.
            </p>
          </div>
          <a
            href="/ideas"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            Browse the full board <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        {isLoading ? (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-card border border-border rounded-xl h-56 mb-4 animate-pulse break-inside-avoid"
              />
            ))}
          </div>
        ) : ideas.length === 0 ? (
          <div className="text-center py-12 bg-card border border-dashed border-border rounded-xl">
            <p className="text-muted-foreground">
              The board is still warming up. <a href="/ideas" className="text-primary underline">Be the first to share an idea</a>.
            </p>
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
            {ideas.map((idea: any) => (
              <IdeaCard
                key={idea.id}
                idea={idea}
                hasVoted={false}
                onVote={() => {}}
                currentUserId={null}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeIdeasStrip;